import { Bell } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotifications, AppNotification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";

const timeAgo = (iso: string) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { data, unreadCount, markRead, markAllRead } = useNotifications();
  const items = data ?? [];

  const handleClick = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.link_path) navigate(n.link_path);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="pop group relative flex items-center justify-center rounded-md p-1.5"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
        >
          <Bell
            className={`h-4 w-4 transition-colors group-hover:fill-current group-focus-visible:fill-current group-active:fill-current ${
              unreadCount > 0 ? "animate-bell-shake" : ""
            }`}
          />
          {unreadCount > 0 && (
            <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-nav" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllRead.mutate()}
              className="text-xs font-medium text-accent hover:underline"
            >
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            You're all caught up.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => (
              <DropdownMenuItem
                key={n.id}
                onClick={() => handleClick(n)}
                className="flex flex-col items-start gap-0.5 whitespace-normal px-3 py-2"
              >
                <div className="flex w-full items-start justify-between gap-2">
                  <span className="text-sm font-semibold leading-tight">
                    {n.title}
                  </span>
                  {!n.read_at && (
                    <span className="mt-1 h-2 w-2 flex-none rounded-full bg-accent" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{n.body}</p>
                <span className="text-[10px] text-muted-foreground">
                  {timeAgo(n.created_at)}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
